/**
 * Why a reseller delete did not happen, in words an operator can act on.
 *
 * Deleting a reseller is refused, not merely failed, in one common case: the
 * reseller has ISSUED invoices, and an issued invoice has to keep the reseller
 * it was issued by (crm-api answers `409 { error: "…" }` — see
 * `Reseller#refuse_or_detach_before_delete`). That sentence is the whole point
 * of the response, so it is what the toast has to show; "Failed to delete
 * reseller" tells the operator nothing they can do next.
 *
 * The three shapes come from crm-api's own error conventions: `{ error }` for a
 * single refusal, `{ errors: [...] }` for the bulk/validation endpoints, and
 * `{ message }` for the handful of older actions. Anything else — a transport
 * failure, an HTML error page, an offline box — falls back to the generic line.
 */
export const RESELLER_DELETE_FALLBACK = "Failed to delete reseller"

export function resellerDeleteErrorMessage(
  error: unknown,
  fallback: string = RESELLER_DELETE_FALLBACK,
): string {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data

  if (typeof data === "string") return data.trim() || fallback
  if (!data || typeof data !== "object") return fallback

  const body = data as { error?: unknown; errors?: unknown; message?: unknown }

  if (typeof body.error === "string" && body.error.trim()) return body.error
  if (Array.isArray(body.errors)) {
    const first = body.errors.find((e) => typeof e === "string" && e.trim())
    if (typeof first === "string") return first
  }
  if (typeof body.message === "string" && body.message.trim()) return body.message

  return fallback
}
